/**
 * Viewer3DBoundary — keeps a failed 3D preview from taking the wizard down.
 *
 * Every viewer creates a WebGL context in an effect. On a device where that
 * fails (blocklisted GPU, in-app WebView with hardware acceleration off,
 * low-end Android) Three.js throws, and an effect error propagates to the
 * nearest error boundary. Before this wrapper that was the wizard's own
 * boundary, so a missing 3D preview cost the visitor the whole estimator.
 *
 * Now each viewer is wrapped individually: the failure is reported to
 * analytics under the '3d-viewer' scope and the visitor sees a short
 * placeholder in the same box. Their estimate is unaffected — the viewers are
 * illustrations only; the math never depends on them.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '../estimator/ErrorBoundary';
import './Room3DViewer.css';

const Viewer3DUnavailable: React.FC = () => {
  const { t } = useTranslation('common');
  return (
    <div className="room-3d-viewer compact">
      <div className="viewer-container viewer-unavailable" role="status">
        {t('viewer.unavailable')}
      </div>
    </div>
  );
};

export function withViewerBoundary<P extends object>(Viewer: React.ComponentType<P>): React.FC<P> {
  const Wrapped: React.FC<P> = (props) => (
    <ErrorBoundary scope="3d-viewer" fallback={<Viewer3DUnavailable />}>
      <Viewer {...props} />
    </ErrorBoundary>
  );
  Wrapped.displayName = `withViewerBoundary(${Viewer.displayName || Viewer.name || 'Viewer'})`;
  return Wrapped;
}
